import React from 'react';
import PropTypes from 'prop-types';
import View from "react-native-web/dist/exports/View";
import Platform from "react-native-web/dist/exports/Platform";
import ImageBackground from "react-native-web/dist/exports/ImageBackground";
import Image from "react-native-web/dist/exports/Image";
import StyleSheet from "react-native-web/dist/exports/StyleSheet";
import { applyShadowToken } from '../ThemeProvider';
import { getTokensPropType, responsiveProps, useResponsiveProp, formatImageSource } from '../utils';
import { a11yProps, viewProps, selectSystemProps } from '../utils/props';
import backgroundImageStylesMap from './backgroundImageStylesMap';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const [selectProps, selectedSystemPropTypes] = selectSystemProps([a11yProps, viewProps]);
const setBackgroundImage = _ref => {
  let {
    src,
    alt,
    backgroundImageResizeMode,
    backgroundImagePosition,
    backgroundImageAlign,
    content,
    cardStyle
  } = _ref;
  const borderRadius = cardStyle?.borderRadius || 0;
  const borderWidth = cardStyle?.borderWidth || 0;
  const adjustedBorderRadius = Math.max(0, borderRadius - borderWidth);

  // For contain mode with position and align, use CSS background properties for web
  if (backgroundImageResizeMode === 'contain' && backgroundImagePosition && backgroundImageAlign) {
    const positionKey = `${backgroundImagePosition}-${backgroundImageAlign}`;
    if (Platform.OS === 'web') {
      // Create background position based on position and align
      let backgroundPosition;
      switch (positionKey) {
        case 'top-start':
          backgroundPosition = 'left top';
          break;
        case 'top-center':
          backgroundPosition = 'center top';
          break;
        case 'top-end':
          backgroundPosition = 'right top';
          break;
        case 'bottom-start':
          backgroundPosition = 'left bottom';
          break;
        case 'bottom-center':
          backgroundPosition = 'center bottom';
          break;
        case 'bottom-end':
          backgroundPosition = 'right bottom';
          break;
        case 'left-center':
          backgroundPosition = 'left center';
          break;
        case 'right-center':
          backgroundPosition = 'right center';
          break;
        default:
          backgroundPosition = 'center center';
      }
      const backgroundImageStyle = {
        backgroundImage: `url(${src})`,
        backgroundSize: 'contain',
        backgroundRepeat: 'no-repeat',
        backgroundPosition,
        borderRadius: adjustedBorderRadius
      };
      return /*#__PURE__*/_jsx(View, {
        style: [staticStyles.imageBackground, backgroundImageStyle],
        role: "img",
        "aria-label": alt,
        children: content
      });
    }
    // For React Native, apply positioning styles with full dimensions
    const positionStyles = backgroundImageStylesMap[positionKey] || {};
    return /*#__PURE__*/_jsxs(View, {
      style: [staticStyles.containContainer, {
        borderRadius: adjustedBorderRadius
      }],
      children: [/*#__PURE__*/_jsx(Image, {
        source: src,
        resizeMode: backgroundImageResizeMode,
        style: [staticStyles.containImage, positionStyles],
        accessible: true,
        accessibilityLabel: alt,
        accessibilityIgnoresInvertColors: true
      }), /*#__PURE__*/_jsx(View, {
        style: staticStyles.contentOverlay,
        children: content
      })]
    });
  }

  // Use ImageBackground for all other resize modes and React Native
  return /*#__PURE__*/_jsx(ImageBackground, {
    source: src,
    imageStyle: {
      borderRadius: adjustedBorderRadius
    },
    resizeMode: backgroundImageResizeMode,
    style: staticStyles.imageBackground,
    accessible: true,
    accessibilityLabel: alt,
    children: content
  });
};

// Ensure explicit selection of tokens
export const selectStyles = _ref2 => {
  let {
    flex,
    backgroundColor,
    borderColor,
    borderRadius,
    borderWidth,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    minWidth,
    shadow,
    backgroundGradient,
    gradient,
    maxHeight,
    overflowY
  } = _ref2;
  return {
    flex,
    backgroundColor,
    borderColor,
    borderRadius,
    borderWidth,
    paddingBottom,
    paddingLeft,
    paddingRight,
    paddingTop,
    minWidth,
    ...applyShadowToken(shadow),
    ...(gradient && Platform.OS === 'web' ? {
      backgroundImage: `linear-gradient(${gradient.angle}deg, ${gradient.stops[0].color}, ${gradient.stops[1].color})`,
      backgroundOrigin: `border-box`,
      boxShadow: `inset 0 1000px white`,
      border: `${borderWidth}px solid transparent`
    } : {}),
    ...(backgroundGradient && Platform.OS === 'web' ? {
      backgroundImage: `linear-gradient(${backgroundGradient.angle}deg, ${backgroundGradient.stops[0].color}, ${backgroundGradient.stops[1].color})`
    } : {}),
    ...(Platform.OS === 'web' ? {
      maxHeight,
      overflowY
    } : {})
  };
};

/**
 * A themeless base component for Card which components can apply theme tokens to. Not
 * intended to be used in apps or sites directly: build themed components on top of this.
 */
const CardBase = /*#__PURE__*/React.forwardRef((_ref3, ref) => {
  let {
    children,
    tokens,
    dataSet,
    backgroundImage,
    ...rest
  } = _ref3;
  const cardStyle = selectStyles(typeof tokens === 'function' ? tokens() : tokens);
  const props = selectProps(rest);
  let content = children;
  const {
    src = '',
    alt = '',
    resizeMode = '',
    position = '',
    align = ''
  } = backgroundImage || {};
  const backgroundImageResizeMode = useResponsiveProp(resizeMode, 'cover');
  const backgroundImagePosition = useResponsiveProp(position);
  const backgroundImageAlign = useResponsiveProp(align);
  const imageSourceViewport = formatImageSource(useResponsiveProp(src));
  if (backgroundImage && src) {
    // When there's a background image, separate the padding from the container style
    // so the image can fill the entire container without padding interference
    const {
      paddingTop,
      paddingBottom,
      paddingLeft,
      paddingRight,
      ...containerStyle
    } = cardStyle;

    // Only create padding wrapper if there's actually padding defined
    const hasPadding = paddingTop || paddingBottom || paddingLeft || paddingRight;
    const paddedContent = hasPadding ? /*#__PURE__*/_jsx(View, {
      style: {
        paddingTop,
        paddingBottom,
        paddingLeft,
        paddingRight
      },
      children: children
    }) : children;
    content = setBackgroundImage({
      src: imageSourceViewport,
      alt,
      backgroundImageResizeMode,
      backgroundImagePosition,
      backgroundImageAlign,
      content: paddedContent,
      cardStyle: containerStyle
    });
    return /*#__PURE__*/_jsx(View, {
      style: containerStyle,
      dataSet: dataSet,
      ref: ref,
      ...props,
      children: content
    });
  }
  return /*#__PURE__*/_jsx(View, {
    style: cardStyle,
    dataSet: dataSet,
    ref: ref,
    ...props,
    children: content
  });
});
CardBase.displayName = 'CardBase';
const staticStyles = StyleSheet.create({
  imageBackground: {
    width: '100%',
    height: '100%'
  },
  contentOverlay: {
    position: 'relative',
    width: '100%',
    height: '100%',
    zIndex: 1
  },
  containContainer: {
    width: '100%',
    height: '100%',
    overflow: 'hidden',
    position: 'relative'
  },
  containImage: {
    position: 'absolute',
    width: '100%',
    height: '100%'
  }
});
CardBase.propTypes = {
  ...selectedSystemPropTypes,
  children: PropTypes.node,
  tokens: getTokensPropType('Card'),
  /**
   * Apply background image to the card.
   */
  backgroundImage: PropTypes.shape({
    // The image src is either a URI string or a number (when a local image src is bundled in IOS or Android app)
    // src is an object when used responsively to provide different image sources for different screen sizes
    src: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.object]).isRequired,
    alt: PropTypes.string,
    resizeMode: responsiveProps.getTypeOptionallyByViewport(PropTypes.oneOf(['cover', 'contain', 'stretch', 'repeat', 'center'])),
    position: responsiveProps.getTypeOptionallyByViewport(PropTypes.oneOf(['bottom', 'left', 'right', 'top'])),
    align: responsiveProps.getTypeOptionallyByViewport(PropTypes.oneOf(['start', 'end', 'center', 'stretch']))
  })
};
export default CardBase;